import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AdminProfile } from '@liyu/shared-types';
import { apiRequest, setAccessToken } from '../api/client';

interface AuthState {
  admin: AdminProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * 静默刷新的单飞 Promise（模块级，不随组件重挂载重置）。
 *
 * 开发模式下 React StrictMode 会双挂载 AuthProvider，两个 effect 几乎同时触发
 * /auth/refresh。刷新令牌是一次性轮换令牌（服务端设计：并发刷新仅一个成功），
 * 第二个请求必然 401，其 catch 分支会把第一个请求刚恢复的登录态清空，
 * 导致每次刷新页面都被踢回登录页。这里让并发调用共享同一次刷新请求，
 * 从根本上避免用同一个一次性令牌发起两次轮换。
 */
let inflightRefresh: Promise<void> | null = null;

/**
 * 后台登录态（P1-01）。
 *
 * - Access Token 只保存在 JavaScript 内存（不写 localStorage/sessionStorage）；
 * - Refresh Token 由服务端写入 HttpOnly + Secure(生产) + SameSite Cookie，前端无法读取；
 * - 页面刷新时调用 /auth/refresh 用 Cookie 静默换取新的 Access Token；
 * - 退出登录调用 /auth/logout 撤销服务端会话并清除 Cookie。
 */
export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [admin, setAdmin] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * 会话提示 Cookie（非 HttpOnly，仅前端可读）：
   * 真正的 Refresh Token 在 HttpOnly Cookie 里、JS 读不到，因此首次进入后台时
   * 无法判断"有没有会话"。若无条件调用 /auth/refresh，未登录访客会在控制台留下
   * 一条 401（用户会当成报错）。这里用一个可读标记避免无谓请求。
   */
  const SESSION_HINT = 'liyu_admin_session';
  const readSessionHint = (): boolean =>
    typeof document !== 'undefined' &&
    document.cookie.split('; ').some((row) => row.startsWith(`${SESSION_HINT}=`));
  const writeSessionHint = (on: boolean): void => {
    if (typeof document === 'undefined') return;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = on
      ? `${SESSION_HINT}=1; path=/; max-age=2592000; SameSite=Lax${secure}`
      : `${SESSION_HINT}=; path=/; max-age=0; SameSite=Lax${secure}`;
  };

  const loadProfile = useCallback(async () => {
    // 没有任何会话痕迹时直接跳过静默刷新，避免无意义的 401 控制台报错
    if (!readSessionHint()) {
      setLoading(false);
      return;
    }
    if (!inflightRefresh) {
      inflightRefresh = (async () => {
        try {
          // 页面刷新后通过 HttpOnly Cookie 静默换取 Access Token
          const refreshed = await apiRequest<{ accessToken: string }>('/auth/refresh', {
            method: 'POST',
          });
          setAccessToken(refreshed.data.accessToken);
          const me = await apiRequest<AdminProfile>('/me');
          setAdmin(me.data);
        } catch {
          setAccessToken(null);
          setAdmin(null);
          writeSessionHint(false);
        } finally {
          inflightRefresh = null;
        }
      })();
    }
    await inflightRefresh;
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await apiRequest<{
      admin: AdminProfile;
      accessToken: string;
    }>('/auth/login', { method: 'POST', body: { email, password } });

    // 只保存 Access Token（内存）；Refresh Token 在 HttpOnly Cookie 中
    setAccessToken(result.data.accessToken);
    setAdmin(result.data.admin);
    writeSessionHint(true);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } catch {
      // 退出失败也要清理内存登录态
    }
    setAccessToken(null);
    setAdmin(null);
    writeSessionHint(false);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ admin, loading, login, logout }),
    [admin, loading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth 必须在 AuthProvider 内使用');
  }
  return context;
}
